import React, { useState } from 'react';
import { Check, Loader2, LogIn, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const colors = {
  bg: '#0c0c0d',
  card: '#111112',
  cardBorder: '#1f1f21',
  text: '#f5f5f5',
  subtext: '#c8c8c8',
  muted: '#9ea3aa',
  green: '#8BE98D',
  greenDark: '#2f6b30',
  btnText: '#0a0f0a',
};

const TierBadge = ({ children }) => (
  <span
    className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.3px]"
    style={{ backgroundColor: colors.greenDark, color: colors.green }}
  >
    {children}
  </span>
);

const PricingCard = ({ tierName, badges = [], price, period, secondaryPrice, features, buttonText, onButtonClick, isLoading = false }) => {
  return (
    <div
      className="relative flex flex-col rounded-2xl border p-6 md:p-10 shadow-[0_18px_38px_rgba(0,0,0,0.45)] transition-transform duration-200 hover:-translate-y-1"
      style={{ background: colors.card, borderColor: colors.cardBorder, minHeight: 440 }}
    >
      <div className="flex min-h-[40px] items-center justify-between gap-4 flex-wrap">
        <div className="text-base md:text-[18px] font-semibold" style={{ color: colors.text }}>
          {tierName}
        </div>
        {badges.length > 0 ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {badges.map((b) => (
              <TierBadge key={b}>{b}</TierBadge>
            ))}
          </div>
        ) : (
          <div />
        )}
      </div>

      <div className="mt-5 md:mt-7">
        <div className="flex items-end gap-2">
          <div className="tabular-nums text-[42px] md:text-[56px] font-bold leading-none" style={{ color: colors.text }}>
            {price}
          </div>
          {period ? (
            <div className="pb-1 text-base md:text-[20px]" style={{ color: colors.muted }}>
              {period}
            </div>
          ) : null}
        </div>
        {secondaryPrice ? (
          <div className="mt-2 md:mt-3 text-xs md:text-[14px]" style={{ color: colors.muted }}>
            {secondaryPrice}
          </div>
        ) : null}
      </div>

      <ul className="mt-6 md:mt-8 space-y-3 md:space-y-[14px]">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3">
            <Check className="mt-[2px] h-4 w-4 md:h-[18px] md:w-[18px]" aria-hidden="true" style={{ color: colors.green }} strokeWidth={2} />
            <span className="text-sm md:text-[15px] leading-[1.5]" style={{ color: colors.subtext }}>
              {f}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex-1" />

      <button
        type="button"
        onClick={onButtonClick}
        disabled={isLoading}
        className="mt-8 md:mt-10 h-12 w-full rounded-[10px] text-sm md:text-[15px] font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9be38f]/60 disabled:opacity-70 disabled:cursor-not-allowed"
        style={{
          background: colors.green,
          color: colors.btnText,
          boxShadow: '0 8px 28px rgba(139,233,141,0.25)',
          border: 'none',
          outline: 'none',
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.boxShadow = '0 10px 32px rgba(139,233,141,0.32)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '0 8px 28px rgba(139,233,141,0.25)';
          e.currentTarget.style.transform = 'translateY(0px)';
        }}
        onMouseDown={(e) => {
          if (!isLoading) {
            e.currentTarget.style.transform = 'translateY(0px) scale(0.98)';
          }
        }}
        onMouseUp={(e) => {
          if (!isLoading) {
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        }}
        aria-label={`${tierName} plan action: ${buttonText}`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Processing...
          </span>
        ) : (
          buttonText
        )}
      </button>
    </div>
  );
};

// Login-required modal
const LoginRequiredModal = ({ isOpen, onClose, onLogin }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-[#282929] rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 animate-in fade-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5 text-[#5a5a5a] dark:text-white/70" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-[rgba(124,182,135,0.15)] dark:bg-[rgba(155,227,143,0.12)]">
            <LogIn className="w-8 h-8 text-[#4a8a54] dark:text-[#9be38f]" />
          </div>
        </div>

        {/* Content */}
        <div className="text-center">
          <h3
            className="text-2xl font-bold text-[#1a1a1a] dark:text-white mb-2"
            style={{ fontFamily: "'Martel Sans', sans-serif" }}
          >
            Sign in to continue
          </h3>
          <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm leading-relaxed mb-8">
            You need to be logged in to purchase a plan. Create an account or sign in to get started with Pluckr.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={onLogin}
              className="w-full py-3 px-6 rounded-xl font-semibold text-sm text-white transition-all hover:-translate-y-0.5"
              style={{ background: '#7cb687', boxShadow: '0 4px 14px rgba(124,182,135,0.3)' }}
            >
              Sign in / Create account
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 px-6 rounded-xl font-semibold text-sm text-[#5a5a5a] dark:text-white/80 bg-[#f5f5f5] dark:bg-white/10 hover:bg-[#ebebeb] dark:hover:bg-white/15 transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

  const PricingSection = ({ testId = 'pricing-section' }) => {
    const [showLoginModal, setShowLoginModal] = useState(false);
    const navigate = useNavigate();

    const tiers = [
      {
        tierName: 'Starter',
        badges: [],
        price: '$4',
        period: '',
        secondaryPrice: 'For students, builders trying Pluckr',
        features: ['Access to Limited MCPs', 'Limited MCP calls / month', 'Community support'],
        buttonText: 'Pluck Now',
        tier: 'starter',
      },
      {
        tierName: 'Pro',
        badges: [],
        price: '$15',
        period: '/month',
        secondaryPrice: 'For startups, serious devs',
        features: [
          'Full access to core MCPs (Payments, Auth, Design, Infra)',
          'Higher MCP usage limits',
          'Early access to new MCPs',
          'Priority updates',
          'Email support',
        ],
        buttonText: 'Join waitlist',
        tier: 'pro',
      },
    ];

    return (
      <section
        className="relative w-full overflow-hidden"
        data-testid={testId}
        style={{ background: colors.bg, fontFamily: 'Inter, -apple-system, system-ui, Segoe UI, Roboto, sans-serif' }}
      >
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(900px 520px at 15% 0%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.00) 60%), radial-gradient(700px 460px at 90% 15%, rgba(139,233,141,0.08) 0%, rgba(139,233,141,0.00) 55%)',
            filter: 'blur(20px)',
          }}
        />

        <div className="relative z-10 mx-auto max-w-[1200px] px-4 py-16 md:py-24">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: colors.green }}>
              Pricing
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold mt-3 tracking-tight"
              style={{ color: colors.text, fontFamily: "'Martel Sans', sans-serif" }}
            >
              Simple plans for every builder
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-[800px] mx-auto">
            {tiers.map((tier) => (
              <PricingCard
                key={tier.tierName}
                {...tier}
                isLoading={false}
                onButtonClick={() => {
                  if (tier.buttonText === 'Pluck Now') {
                    // Users on landing page are always logged out (logged-in users are redirected to dashboard)
                    // So always show login modal - no payment here
                    setShowLoginModal(true);
                  } else if (tier.buttonText === 'Join waitlist') {
                    const ctaEl = document.getElementById('cta');
                    if (ctaEl) {
                      ctaEl.scrollIntoView({ behavior: 'smooth' });
                    }
                  }
                }}
              />
            ))}
          </div>
        </div>

        {/* Login Required Modal */}
        <LoginRequiredModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLogin={() => {
            setShowLoginModal(false);
            navigate('/early-access');
          }}
        />
      </section>
    );
  };

  export default PricingSection;
